import { useApolloClient } from '@apollo/react-hooks'
import PortalLearningPaths from '@classes/LearningPaths'
import MyBerlitzLessons from '@classes/Lessons'
import PortalStudentProfile from '@classes/StudentProfile'
import { LMSLearningPlans } from '@interfaces/LearningPath'
import QueryResult from '@interfaces/QueryResult'
import { ScheduleSession } from '@interfaces/ScheduleSession'
import { StoreProps } from '@interfaces/StoreState'
import { GET_LEARNING_PATH_BY_LPIDS } from '@queries/learningPath'
import { GET_SCHEDULES_PER_PROGRAM } from '@queries/schedules'
import { GET_SIMPLE_STUDENT_PROFILE } from '@queries/userProfile'
import { MY_BERLITZ_ENV, PROFILE_TYPE } from '@utils/constants'
import learningPath from '@utils/stubdata/learningPath.json'
import React, { useEffect, useState } from 'react'
import { Query } from 'react-apollo'
import { useSelector } from 'react-redux'

export interface QueryPageResults {
  loading: boolean
  data: LMSLearningPlans[]
  error?: Error
  StudentProfile?: PortalStudentProfile
  sessions?: ScheduleSession[]
}

interface Props {
  children: React.ReactNode
  type?: string
  id?: string
  StudentProfile?: PortalStudentProfile
}

const STUB = false

const MainQuery: React.FC<Props> = ({ children, type, StudentProfile }) => {
  const client = useApolloClient()
  const [sessions, setSessions] = useState<ScheduleSession[]>([])
  const [isSchedLoading, setSchedLoading] = useState(true)
  const {
    learningPathLoading: lpLoading,
    learningPath: lpData,
    learningPathError: lpError,
  } = useSelector(({ preloadedData }: StoreProps) => preloadedData)
  const userLocalLPId = localStorage.getItem(`${StudentProfile?.SFId}-lpid`)
  const userLocalProgramId = localStorage.getItem(`${StudentProfile?.SFId}-programid`)
  const portalSelectedMaterial = JSON.parse(localStorage.getItem('portal-selected-material') || '{}')?.[
    StudentProfile?.SFId!
  ]
  const Ids = userLocalProgramId
    ? StudentProfile?.Programs?.[userLocalProgramId!]?.LPIds?.join('|')
    : portalSelectedMaterial || userLocalLPId

  useEffect(() => {
    ;(async () => {
      const fetchSchedules = async () => {
        const fetchSchedulesPromise = StudentProfile?.PortalPrograms?.FlexPrograms?.map(async (Program) => {
          return await client.query({
            query: GET_SCHEDULES_PER_PROGRAM,
            variables: {
              Id: StudentProfile?.SFId,
              FromDate: Program.ProgramEnrollDate,
              EndDate: Program.getSessionSearchEndDate(StudentProfile?.Timezone),
              ProgramIds: Program.ProgramId,
              UserId: StudentProfile?.SFId
            },
            fetchPolicy: 'no-cache',
          })
        })

        // @ts-ignore
        return Promise.all(fetchSchedulesPromise)
      }

      const scheds = await fetchSchedules()
      setSessions(
        Array.prototype.concat.apply(
          [],
          // @ts-ignore
          scheds.map((sched) => sched.data?.getSchedulesId)
        )
      )
      setSchedLoading(false)
    })()
  }, [])

  return ((!lpLoading && !lpData) || lpError) && StudentProfile?.SFId && Ids ? (
    <Query
      query={GET_LEARNING_PATH_BY_LPIDS}
      variables={{
        StudentId: StudentProfile?.SFId,
        IdType: type,
        Ids,
        berlitzEnv: StudentProfile?.MyBerlitzEnv ?? MY_BERLITZ_ENV.DEFAULT,
      }}
      fetchPolicy="no-cache"
    >
      {({ loading, data, error }: QueryResult) =>
        // @ts-ignore
        children({
          loading: loading || isSchedLoading || !StudentProfile,
          data: STUB ? learningPath.data.getLearningPath : data?.getLearningPathByIds,
          error,
          StudentProfile,
          sessions,
          LearningPaths: new PortalLearningPaths(
            STUB ? learningPath.data.getLearningPath : data?.getLearningPathByIds,
            StudentProfile,
            new MyBerlitzLessons(sessions)
          ),
        })
      }
    </Query>
  ) : (
    <>
      {
        // @ts-ignore
        children({
          loading: lpLoading || isSchedLoading || !StudentProfile,
          data: STUB ? learningPath.data.getLearningPath : lpData?.getLearningPath,
          error: lpError,
          StudentProfile,
          sessions,
          LearningPaths: new PortalLearningPaths(
            lpData?.getLearningPath,
            StudentProfile,
            new MyBerlitzLessons(sessions)
          ),
        })
      }
    </>
  )
}

export const QueryLearningPathByInstructor: React.FC<Props> = ({ id, children }) => (
  <Query
    query={GET_SIMPLE_STUDENT_PROFILE}
    variables={{
      id,
    }}
    fetchPolicy="no-cache"
  >
    {(res: QueryResult) => {
      if (res.loading || res.error) {
        // @ts-ignore
        return children({
          loading: res.loading,
          error: res.error,
        })
      }

      const StudentProfile = new PortalStudentProfile(res.data?.getUserProfile?.StudentProfile?.StudentProfileInfo)
      return (
        <MainQuery type={PROFILE_TYPE.INSTRUCTOR} StudentProfile={StudentProfile}>
          {children}
        </MainQuery>
      )
    }}
  </Query>
)

const QueryLearningPath: React.FC<Props> = ({ children }) => {
  const StudentProfile = useSelector(
    ({ userProfile }: StoreProps) => new PortalStudentProfile(userProfile?.info?.StudentProfile?.StudentProfileInfo)
  )

  return (
    <MainQuery type={PROFILE_TYPE.STUDENT} StudentProfile={StudentProfile}>
      {children}
    </MainQuery>
  )
}

export default QueryLearningPath
